import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Send, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import {
  usePlatformTickets, useTicketMessages, useSendTicketMessage,
  useCloseTicket, useReopenTicket, useOpenTicketCount,
  type SupportTicket,
} from "@/hooks/useSupportTickets";

const priorityColor: Record<string, string> = {
  low: "bg-muted text-muted-foreground",
  normal: "bg-secondary text-secondary-foreground",
  high: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  urgent: "bg-destructive text-destructive-foreground",
};

const TicketThread = ({ ticket, onClose }: { ticket: SupportTicket; onClose: () => void }) => {
  const { data: messages = [], isLoading } = useTicketMessages(ticket.id);
  const sendMessage = useSendTicketMessage();
  const closeTicket = useCloseTicket();
  const reopenTicket = useReopenTicket();
  const [reply, setReply] = useState("");

  const handleSend = async () => {
    if (!reply.trim()) return;
    try {
      await sendMessage.mutateAsync({ ticketId: ticket.id, body: reply.trim(), senderType: "admin" });
      setReply("");
      toast.success("Message sent");
    } catch {
      toast.error("Failed to send message");
    }
  };

  const handleClose = async () => {
    try { await closeTicket.mutateAsync(ticket.id); toast.success("Ticket closed"); onClose(); }
    catch { toast.error("Failed to close"); }
  };

  const handleReopen = async () => {
    try { await reopenTicket.mutateAsync(ticket.id); toast.success("Ticket reopened"); }
    catch { toast.error("Failed to reopen"); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b pb-3 mb-3 space-y-1">
        <p className="text-xs text-muted-foreground">
          {ticket.ticket_number ? `TKT-${String(ticket.ticket_number).padStart(5, "0")}` : ""} · {ticket.company_name ?? "Unknown tenant"}
        </p>
        <p className="font-semibold">{ticket.subject}</p>
        {ticket.body && <p className="text-sm text-muted-foreground">{ticket.body}</p>}
        <div className="flex gap-2 pt-1">
          <Badge variant={ticket.status === "open" ? "destructive" : "secondary"} className="capitalize text-xs">{ticket.status}</Badge>
          <Badge className={`${priorityColor[ticket.priority]} capitalize text-xs`}>{ticket.priority}</Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2 mb-3">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-md p-3 text-sm ${
                m.sender_type === "admin"
                  ? "bg-primary/10 border-l-2 border-primary ml-4"
                  : "bg-muted/50 mr-4"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={m.sender_type === "admin" ? "default" : "secondary"} className="text-[10px]">
                  {m.sender_type === "admin" ? "You (Admin)" : "Tenant"}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(m.created_at).toLocaleString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </div>
          ))
        )}
      </div>

      <div className="border-t pt-3 space-y-2">
        <div className="flex gap-2">
          <Textarea placeholder="Type reply…" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} className="text-sm" />
          <Button size="icon" className="shrink-0 self-end" onClick={handleSend} disabled={!reply.trim() || sendMessage.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          {ticket.status === "open" ? (
            <Button variant="outline" size="sm" onClick={handleClose}><Check className="h-3.5 w-3.5 mr-1" /> Close Ticket</Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleReopen}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</Button>
          )}
        </div>
      </div>
    </div>
  );
};

const PlatformSupport = () => {
  const { data: tickets = [] } = usePlatformTickets();
  const openTicketCount = useOpenTicketCount();
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketFilter, setTicketFilter] = useState<"open" | "closed" | "all">("open");

  const filteredTickets = tickets.filter((t) => ticketFilter === "all" || t.status === ticketFilter);

  return (
    <div className="space-y-6">
      <SEO title="Support Tickets" description="Manage support tickets" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Support Tickets</h1>
          <p className="text-sm text-muted-foreground">
            {openTicketCount > 0 ? `${openTicketCount} open ticket${openTicketCount !== 1 ? "s" : ""}` : "No open tickets"}
          </p>
        </div>
        <div className="flex gap-1.5">
          {(["open", "closed", "all"] as const).map((s) => (
            <Badge
              key={s}
              variant={ticketFilter === s ? "default" : "outline"}
              className="cursor-pointer capitalize text-xs"
              onClick={() => setTicketFilter(s)}
            >
              {s}
            </Badge>
          ))}
        </div>
      </div>

      {filteredTickets.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            No {ticketFilter !== "all" ? ticketFilter : ""} support tickets.
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Ticket #</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.map((t) => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-accent/50" onClick={() => setSelectedTicket(t)}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {t.ticket_number ? `TKT-${String(t.ticket_number).padStart(5, "0")}` : "—"}
                  </TableCell>
                  <TableCell className="text-xs">{t.company_name ?? "—"}</TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{t.subject}</p>
                    {t.body && <p className="text-xs text-muted-foreground line-clamp-1">{t.body}</p>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${priorityColor[t.priority] ?? ""} text-xs capitalize`}>{t.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={t.status === "open" ? "destructive" : "secondary"} className="text-xs capitalize">{t.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Sheet open={!!selectedTicket} onOpenChange={(o) => { if (!o) setSelectedTicket(null); }}>
        <SheetContent className="sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Ticket Conversation</SheetTitle>
          </SheetHeader>
          {selectedTicket && (
            <TicketThread ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PlatformSupport;
